import { useEffect } from 'react';
import { Typography, Tabs, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import NodeSubscription from './NodeSubscription';
import PersonFields from './PersonFields';
import FederationPeers from './FederationPeers';
import Consents from './Consents';
import NodeInviteEmail from './NodeInviteEmail';
import NodeInvites from './NodeInvites';
import NodeBranding from './NodeBranding';
import NodeDns from './NodeDns';
import NodeApps from './NodeApps';
import MapPage from '../Map/MapPage';
import { useSuperAdmins } from '../../hooks/use_super_admins';
import { useNodeInfo } from '../../hooks/use_node_info';
import NodeLogo from '../Node/NodeLogo';

const { Title, Text } = Typography;

/**
 * Node (server) administration settings, split out from the personal Account
 * page, one tab per panel. The active tab lives in the URL
 * (/node-account/<tab>) so each tab is linkable and survives reloads/back.
 * Every panel is superadmin-only and self-hides for anyone else (each one's
 * list endpoint 403s), so this page is safe to mount for any logged-in user.
 */
function NodeAccount() {
    const { tab } = useParams();
    const navigate = useNavigate();
    const { isServerAdmin, loading: adminLoading, error: adminError } = useSuperAdmins();
    const { ready: nodeInfoReady, nodeName, featureSubscription, federationEnabled, featureBranding, featureRoadmapHints } = useNodeInfo();
    // "Warmdata settings" — the node's own name, capitalised, once /info has
    // said what it is; a neutral label while it loads.
    const title = nodeName ? `${nodeName.charAt(0).toUpperCase()}${nodeName.slice(1)} settings` : 'Node settings';
    // Both checks decide which tabs exist; until they settle, the tab bar would
    // appear, grow and shrink (the node flags are permissive-until-known).
    const settled = nodeInfoReady && !adminLoading;

    // Only offer tabs that would render something — Subscription and
    // Federation hide themselves when their feature is off. forceRender mounts
    // every panel up front so its data is already loaded when you switch to it.
    // Super admins are managed from the browse page (Homepage's admin
    // actions), so there is no tab for them here.
    const tabs = [
        // First when on, so the page opens on the node's own mark; behind
        // feature_branding (the backend 404s the logo routes when it is off).
        featureBranding && { key: 'branding', label: 'Branding', children: <NodeBranding />, forceRender: true },
        { key: 'consents', label: 'Consents', children: <Consents />, forceRender: true },
        { key: 'person-fields', label: 'Person fields', children: <PersonFields />, forceRender: true },
        { key: 'dns', label: 'DNS', children: <NodeDns />, forceRender: true },
        // The apps installed in the node itself, for everyone signed in here.
        { key: 'apps', label: 'Apps', children: <NodeApps />, forceRender: true },
        // Invite people to the node itself: by e-mail or a link; they join
        // from their node menu or the link, with no approval step.
        { key: 'invites', label: 'Invites', children: <NodeInvites />, forceRender: true },
        { key: 'invite-email', label: 'Invite email', children: <NodeInviteEmail />, forceRender: true },
        featureSubscription && { key: 'subscription', label: 'Subscription', children: <NodeSubscription />, forceRender: true },
        federationEnabled && { key: 'federation', label: 'Federation', children: <FederationPeers />, forceRender: true },
        // Behind feature_roadmap_hints, alongside the rest of the roadmap UI.
        // Last, as reference rather than something to edit.
        featureRoadmapHints && { key: 'map', label: 'Map', children: <MapPage />, forceRender: true },
    ].filter(Boolean);

    const activeKey = tabs.some(t => t.key === tab) ? tab : tabs[0].key;

    // Bare /node-account (or an unknown/unavailable tab) -> the first tab's URL,
    // replacing history so Back doesn't bounce through the redirect. Waits for
    // the tab list to settle so it never redirects based on provisional flags.
    useEffect(() => {
        if (settled && isServerAdmin && tab !== activeKey) {
            navigate(`/node-account/${activeKey}`, { replace: true });
        }
    }, [settled, isServerAdmin, tab, activeKey, navigate]);

    return (
        <div style={{ margin: 20, maxWidth: 720 }}>
            <Title level={4} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <NodeLogo size={28} />
                {title}
            </Title>
            {!settled ? (
                <Spin style={{ display: 'block', marginTop: 24 }} />
            ) : isServerAdmin ? (
                <Tabs
                    activeKey={activeKey}
                    onChange={key => navigate(`/node-account/${key}`)}
                    items={tabs}
                />
            ) : adminError ? (
                // A 5xx/network failure, not a 403 — don't claim they aren't an admin.
                <Text type="secondary">Couldn't check your permissions. Refresh the page to try again.</Text>
            ) : (
                <Text type="secondary">Node settings are only available to super admins.</Text>
            )}
        </div>
    );
}

export default NodeAccount;
